/**
 * Catchball proposal decisions — the ONE client path to the three decision
 * RPCs (migration 763, owner ruling 2026-09-22 "one accept with options").
 *
 *   accept  → catchball_accept               (plain, with conditions, provisional,
 *                                              or ratifying a provisional accept)
 *   reject  → catchball_reject
 *   return  → catchball_return_for_revision
 *
 * Each covers item proposals AND proposal packets. The server derives the loop,
 * cycle and workspace from the proposal row and acts as auth.uid(), so callers
 * pass only the proposal id and the decision's own content. Every refusal comes
 * back as `{ ok: false, error, code }` — never as a thrown error.
 *
 * Both catchballStore (item / inbox surfaces) and proposalPacketStore (packet
 * review surfaces) call these; do not add another direct `.rpc(...)` for a
 * Catchball decision.
 */

import { getSafeSupabaseClient } from '../supabaseSync';
import { getTypedSupabaseClient } from '../typedSupabaseClient';
import { actRightFromError, readActRight, type ActRight } from '../actRight';
import {
  isProposalAwaitingDecision,
  type ProposalStatus,
} from '../../types/prioritization/catchballProposal';

export interface CatchballDecisionResult {
  ok: boolean;
  /** Plain-language reason when `ok` is false. */
  error?: string;
  /** Server refusal code: not_found | not_authorized | invalid | already_decided | failed. */
  code?: string;
  /** The RPC's full success payload (proposal / loop rows, report_result, …). */
  data?: Record<string, unknown>;
}

const OFFLINE: CatchballDecisionResult = {
  ok: false,
  code: 'offline',
  error: 'Not connected to the server. Check your connection and try again.',
};

async function callDecisionRpc(
  fn:
    | 'catchball_accept'
    | 'catchball_reject'
    | 'catchball_return_for_revision'
    | 'catchball_submit_proposal'
    | 'catchball_draft_proposal',
  args: Record<string, unknown>,
  fallback: string,
): Promise<CatchballDecisionResult> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return OFFLINE;
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    return { ok: false, code: error.code ?? 'error', error: error.message || fallback };
  }
  const payload = (data ?? null) as Record<string, unknown> | null;
  if (!payload || payload.success !== true) {
    return {
      ok: false,
      code: typeof payload?.code === 'string' ? payload.code : undefined,
      error: typeof payload?.error === 'string' && payload.error ? payload.error : fallback,
    };
  }
  return { ok: true, data: payload };
}

/** Pre-click mirror of the server's decision gate (763 internal_catchball_awaiting_decision). */
export function canDecideProposal(status: string | null | undefined): boolean {
  return isProposalAwaitingDecision((status ?? '') as ProposalStatus);
}

/**
 * Pre-click mirror of the full (ratifying) accept's gate: awaiting a decision,
 * or already provisionally accepted (legacy packet rows show 'accepted' while
 * the loop is still 'accepted_provisional').
 */
export function canRatifyProposal(
  status: string | null | undefined,
  loopState?: string | null,
): boolean {
  return (
    canDecideProposal(status) ||
    status === 'accepted_provisional' ||
    (status === 'accepted' && loopState === 'accepted_provisional')
  );
}

/**
 * May the signed-in person accept this proposal — and ratify it, or only
 * accept it provisionally? `can_accept_catchball` (migration 987) answers with
 * the rule `catchball_accept` acts on: a designated recipient, the loop's
 * requester, the cycle owner, a workspace admin or a catchball.accept grant,
 * and then the cycle's "Items / selection" decision right when one is set.
 * Takes a proposal id, or a loop id (its current proposal).
 */
export async function fetchAcceptRight(proposalOrLoopId: string): Promise<ActRight> {
  try {
    const { data, error } = await getTypedSupabaseClient().rpc('can_accept_catchball', {
      p_loop_or_proposal: proposalOrLoopId,
    });
    if (error) return actRightFromError(error, 'accept this proposal');
    return readActRight(data, 'You may not accept this proposal.');
  } catch (err) {
    return actRightFromError({ message: err instanceof Error ? err.message : String(err) }, 'accept this proposal');
  }
}

export interface AcceptCatchballProposalInput {
  proposalId: string;
  /** Accept provisionally: nothing is ratified, the ball goes back to the working tier. */
  provisional?: boolean;
  /** Optional either way; recorded on the proposal, thread and governance event. */
  conditions?: string | null;
  rationale?: string | null;
  ratificationAuthority?: string | null;
  /** Structured blockers the working tier must still clear (provisional packet accept). */
  blockedReasons?: unknown;
  actorName?: string | null;
}

export function acceptCatchballProposal(input: AcceptCatchballProposalInput): Promise<CatchballDecisionResult> {
  return callDecisionRpc(
    'catchball_accept',
    {
      p_proposal_id: input.proposalId,
      p_provisional: input.provisional === true,
      p_conditions: input.conditions?.trim() || null,
      p_rationale: input.rationale?.trim() || null,
      p_ratification_authority: input.ratificationAuthority ?? null,
      p_blocked_reasons: input.blockedReasons ?? null,
      p_actor_name: input.actorName ?? null,
    },
    input.provisional ? 'The proposal could not be accepted.' : 'The proposal could not be ratified.',
  );
}

export function rejectCatchballProposal(input: {
  proposalId: string;
  rationale: string;
  actorName?: string | null;
}): Promise<CatchballDecisionResult> {
  if (!input.rationale.trim()) {
    return Promise.resolve({ ok: false, code: 'invalid', error: 'A reason is required to reject.' });
  }
  return callDecisionRpc(
    'catchball_reject',
    { p_proposal_id: input.proposalId, p_rationale: input.rationale.trim(), p_actor_name: input.actorName ?? null },
    'The proposal could not be rejected.',
  );
}

export function returnCatchballProposal(input: {
  proposalId: string;
  revisionGuidance?: string | null;
  structuredBlockers?: unknown;
  actorName?: string | null;
}): Promise<CatchballDecisionResult> {
  const guidance = input.revisionGuidance?.trim() || null;
  const blockers = input.structuredBlockers ?? null;
  if (!guidance && !blockers) {
    return Promise.resolve({
      ok: false,
      code: 'invalid',
      error: 'Say what needs to change before returning it.',
    });
  }
  return callDecisionRpc(
    'catchball_return_for_revision',
    {
      p_proposal_id: input.proposalId,
      p_revision_guidance: guidance,
      p_structured_blockers: blockers,
      p_actor_name: input.actorName ?? null,
    },
    'The proposal could not be returned.',
  );
}

/**
 * Draft a proposal — the one client path to catchball_draft_proposal
 * (migration 772), shared by catchballStore ("Send this up" packages a
 * finished sitting) and proposalPacketStore (a packet built from the
 * opportunity solution tree).
 *
 * The server reads the ranking, the item pool, the session report, the resume
 * case and the revision parent from the database; callers pass only what the
 * person chose. It also points the loop at the new draft in the same
 * transaction, so a refusal can no longer leave an orphan draft behind.
 */
export function draftCatchballProposal(input: {
  loopId: string;
  mode: 'session' | 'ost';
  fields: Record<string, unknown>;
  actorName?: string | null;
}): Promise<CatchballDecisionResult> {
  return callDecisionRpc(
    'catchball_draft_proposal',
    {
      p_loop_id: input.loopId,
      p_mode: input.mode,
      p_fields: input.fields,
      p_actor_name: input.actorName ?? null,
    },
    'The proposal could not be packaged.',
  );
}

/**
 * Submit (send up) a proposal — the one client path to catchball_submit_proposal,
 * shared by catchballStore and proposalPacketStore.
 */
export function submitCatchballProposal(input: {
  proposalId: string;
  actorId: string;
  rationale?: string | null;
  tradeoffs?: object[] | null;
}): Promise<CatchballDecisionResult> {
  const args: Record<string, unknown> = { p_packet_id: input.proposalId, p_actor_id: input.actorId };
  if (input.rationale != null) args.p_rationale = input.rationale;
  if (input.tradeoffs != null) args.p_tradeoffs = input.tradeoffs.length > 0 ? input.tradeoffs : null;
  return callDecisionRpc('catchball_submit_proposal', args, 'The proposal could not be submitted.');
}
